import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  useReactFlow,
  useNodesInitialized,
  useStore
} from 'reactflow';
import 'reactflow/dist/style.css';
import { WorkflowNode } from '../molecules/WorkflowNode';
import { TerminalNode } from '../molecules/TerminalNode';
import { NodeConfigModal } from '../molecules/NodeConfigModal';
import { ConditionalEdge } from '../molecules/ConditionalEdgeButton';
import { ReadonlyLabeledEdge } from '../molecules/ReadonlyLabeledEdge';
import { ConditionalEdgeModal } from '../molecules/ConditionalEdgeModal';
import { DecisionNode } from '../molecules/DecisionNode';
import { Button } from '../atoms';
import { getLayoutedElements } from '../utils/layout';
import { WorkflowActionsContext } from '../contexts/WorkflowActionsContext';
import './GraphFlowEditor.css';

function DefaultZoomOut({ zoomMultiplier = 0.95 }) {
  const { fitView, getViewport, setViewport } = useReactFlow();
  const initialized = useNodesInitialized();
  const width = useStore((s) => s.width);
  const height = useStore((s) => s.height);
  const hasRun = useRef(false);
  useEffect(() => {
    if (hasRun.current || !initialized || !fitView || !setViewport) return;
    hasRun.current = true;
    fitView();
    const id = setTimeout(() => {
      const vp = typeof getViewport === 'function' ? getViewport() : null;
      if (vp && typeof vp.zoom === 'number') {
        const nextZoom = Math.max(0.35, vp.zoom * zoomMultiplier);
        // Keep the viewport centered while zooming out.
        if (width > 0 && height > 0) {
          const worldCenterX = (width / 2 - vp.x) / vp.zoom;
          const worldCenterY = (height / 2 - vp.y) / vp.zoom;
          const nextX = width / 2 - worldCenterX * nextZoom;
          const nextY = height / 2 - worldCenterY * nextZoom;
          setViewport({ x: nextX, y: nextY, zoom: nextZoom }, { duration: 0 });
          return;
        }
        setViewport({ x: vp.x, y: vp.y, zoom: nextZoom }, { duration: 0 });
      }
    }, 50);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, width, height]);
  return null;
}

// Re-centers + zooms to fit ALL content whenever `signal` changes (e.g. the
// canvas was resized into/out of fullscreen). Two delayed passes let the
// container's resize settle before fitView reads the new dimensions. Skips the
// initial mount — DefaultZoomOut owns the first-load framing.
function FitOnSignal({ signal, padding = 0.15 }) {
  const { fitView } = useReactFlow();
  const skipFirst = useRef(true);
  useEffect(() => {
    if (skipFirst.current) { skipFirst.current = false; return undefined; }
    if (!fitView) return undefined;
    const t1 = setTimeout(() => fitView({ padding, duration: 200 }), 90);
    const t2 = setTimeout(() => fitView({ padding, duration: 0 }), 240);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal]);
  return null;
}

const EMPTY_NODE_CONFIG = {};
const EMPTY_CUSTOM_NODE_TYPES = {};

// Convert snake_case to Title Case
const toTitleCase = (str) => str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Default insertable node categories
const DEFAULT_INSERTABLE_CATEGORIES = [
  {
    id: 'ai_agent',
    label: 'AI Agent',
    nodeType: 'ai_agent',
    description: 'Add a new AI agent node'
  },
  {
    id: 'decision',
    label: 'Decision Branch',
    nodeType: 'decision',
    description: 'Add conditional routing logic'
  }
];

export const GraphFlowEditor = ({
  graphType = 'analysis',
  initialNodes = [],
  initialEdges = [],
  nodeConfigs: initialNodeConfigs = {},
  stateSchema = null,
  insertableCategories: insertableCategoriesProp,
  onChange,
  onRequestReset,
  readonly = false,
  showResetPanel = true,
  showGraphInfoPanel = true,
  backgroundVariant = 'dots',
  backgroundGap = 16,
  backgroundColor = '#aaa',
  backgroundLineWidth,
  defaultZoomMultiplier = 0.95,
  customNodeTypes = EMPTY_CUSTOM_NODE_TYPES,
  syncFromProps = false,
  // Live node-config editing on an otherwise structure-locked (readonly) graph:
  // the pipeline Overview graph keeps drag/add/delete disabled, but lets a node's
  // prompt / extra instructions / tools be edited and persisted. When an
  // `onSaveNodeConfig(nodeId, config, baseConfig)` callback is supplied the
  // NodeConfigModal opens editable even under `readonly`.
  nodeConfigEditable = false,
  onSaveNodeConfig = null,
  // Bump this number to re-center + zoom-to-fit (e.g. on fullscreen toggle).
  fitSignal = 0
}) => {
  // Use provided categories or default
  const insertableCategories = insertableCategoriesProp || DEFAULT_INSERTABLE_CATEGORIES;
  const reactFlowInstance = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeConfigs, setNodeConfigs] = useState(initialNodeConfigs);

  useEffect(() => {
    if (!syncFromProps) return;
    setNodes(initialNodes);
    setEdges(initialEdges);
    setNodeConfigs(initialNodeConfigs);
  }, [syncFromProps, initialNodes, initialEdges, initialNodeConfigs, setNodes, setEdges]);
  
  // Distribute edges to different target handles based on source position
  const distributeTargetHandles = useCallback((edgesToProcess, currentNodes) => {
    const nodePositions = {};
    currentNodes.forEach(n => { nodePositions[n.id] = n.position; });
    
    // Group edges by target node
    const edgesByTarget = {};
    edgesToProcess.forEach(edge => {
      if (!edgesByTarget[edge.target]) {
        edgesByTarget[edge.target] = [];
      }
      edgesByTarget[edge.target].push(edge);
    });
    
    // Assign target handles based on source position
    return edgesToProcess.map(edge => {
      const targetEdges = edgesByTarget[edge.target];
      
      // If only one edge to this target, use top handle
      if (targetEdges.length === 1) {
        return { ...edge, targetHandle: 'top' };
      }
      
      // For multiple edges, assign based on source X position relative to target
      const targetPos = nodePositions[edge.target];
      const sourcePos = nodePositions[edge.source];
      
      if (!targetPos || !sourcePos) {
        return { ...edge, targetHandle: 'top' };
      }
      
      // Sort all edges to this target by source X position
      const sortedEdges = [...targetEdges].sort((a, b) => {
        const posA = nodePositions[a.source]?.x || 0;
        const posB = nodePositions[b.source]?.x || 0;
        return posA - posB;
      });
      
      const edgeIndex = sortedEdges.findIndex(e => e.id === edge.id);
      
      // Distribute: leftmost -> target-left, rightmost -> target-right, middle -> top
      if (targetEdges.length === 2) {
        return { ...edge, targetHandle: edgeIndex === 0 ? 'target-left' : 'target-right' };
      } else {
        // For 3+ edges, use left for first, right for last, top for middle
        if (edgeIndex === 0) return { ...edge, targetHandle: 'target-left' };
        if (edgeIndex === sortedEdges.length - 1) return { ...edge, targetHandle: 'target-right' };
        return { ...edge, targetHandle: 'top' };
      }
    });
  }, []);
  
  // Apply handle distribution on initial load or when initialEdges change
  // DISABLED: This was overriding loaded edge handles on mount
  // Now we calculate handles dynamically on drag instead
  // useEffect(() => {
  //   if (edges.length > 0 && nodes.length > 0 && !hasDistributed) {
  //     const distributedEdges = distributeTargetHandles(edges, nodes);
  //     setEdges(distributedEdges);
  //     setHasDistributed(true);
  //   }
  // }, [edges, nodes, hasDistributed, distributeTargetHandles, setEdges]);
  
  // Modal state
  const [selectedNode, setSelectedNode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Conditional edge modal state
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);

  // Add-node menu state
  const [addMenuAnchor, setAddMenuAnchor] = useState(null); // { nodeId, x, yBelow, yAbove }
  
  // Context menu state (right-click)
  const [contextMenu, setContextMenu] = useState(null); // { nodeId, x, y }

  
  // Notify parent of changes
  const notifyChange = useCallback((newNodes, newEdges, newNodeConfigs) => {
    if (onChange) {
      onChange({
        nodes: newNodes,
        edges: newEdges,
        nodeConfigs: newNodeConfigs
      });
    }
  }, [onChange]);
  
  // Handle edge connection (if we enable it later)
  const onConnect = useCallback((params) => {
    if (readonly) return;
    const newEdges = addEdge({ ...params, type: 'smoothstep', animated: true }, edges);
    const distributedEdges = distributeTargetHandles(newEdges, nodes);
    setEdges(distributedEdges);
    notifyChange(nodes, distributedEdges, nodeConfigs);
  }, [readonly, nodes, edges, nodeConfigs, notifyChange, setEdges, distributeTargetHandles]);
  
  // Compute which nodes can show the "+" add button and inject runtime data
  const nodesWithAddButton = useMemo(() => {
    return nodes.map(node => {
      // All nodes can add nodes (except setup and decision nodes)
      const canAddAfter = node.id !== 'setup' && node.type !== 'decision';
      const showAddButton = canAddAfter && !readonly;
      // All nodes can be deleted
      const canDelete = !readonly;
      const cfg = nodeConfigs[node.id];
      const toolCount = cfg?.tools?.length || 0;
      const customCode = cfg?.customCode || null;
      // Sub-graph dispatch (parent-child): the CHILD workflow this node runs, so
      // the node can be badged as a sub-workflow. Template-driven — read straight
      // from the serialized node config (engine's dispatchesWorkflow), never
      // hardcoded per slug.
      const dispatchesWorkflow = cfg?.dispatchesWorkflow || null;
      // All nodes are configurable (clickable) - unified node model
      const effectiveConfigurable = true;

      return {
        ...node,
        data: {
          ...node.data,
          showAddButton, nodeId: node.id, toolCount,
          customCode, dispatchesWorkflow, configurable: effectiveConfigurable, canDelete
        }
      };
    });
  }, [nodes, readonly, nodeConfigs]);

  // Remove a node and all its connected edges
  const handleRemoveNode = useCallback((nodeId) => {
    const removedNode = nodes.find(n => n.id === nodeId);
    if (!removedNode) return;

    const filteredEdges = edges.filter(e => e.source !== nodeId && e.target !== nodeId);
    const filteredNodes = nodes.filter(n => n.id !== nodeId);

    const updatedConfigs = { ...nodeConfigs };
    delete updatedConfigs[nodeId];

    const distributedEdges = distributeTargetHandles(filteredEdges, filteredNodes);

    setNodes(filteredNodes);
    setEdges(distributedEdges);
    setNodeConfigs(updatedConfigs);
    notifyChange(filteredNodes, distributedEdges, updatedConfigs);
  }, [nodes, edges, nodeConfigs, notifyChange, setNodes, setEdges, distributeTargetHandles]);

  // Handle node click -- open config modal or conditional edge modal
  const onNodeClick = useCallback((event, node) => {
    // START / END terminal markers have no config — clicking them is a no-op.
    if (node.type === 'terminal') return;
    // Read-only surfaces (e.g. the pipeline Overview graph) still open the
    // EXISTING NodeConfigModal so a node's prompt / execute.js / tools can be
    // INSPECTED — view-only (the modal hides Save + makes its editors read-only
    // when `readOnly` is passed). Edit-only interactions stay disabled.
    if (readonly) {
      // Decision nodes open the conditional-logic modal (view-only) so the
      // branch routing code is inspectable; other nodes open the config modal.
      if (node.type === 'decision') {
        const decisionEdges = edges.filter(e => e.source === node.id);
        if (decisionEdges.length > 0) {
          setSelectedEdge({
            ...decisionEdges[0],
            source: node.id,
            data: {
              ...decisionEdges[0].data,
              conditionalCode: decisionEdges[0].data?.conditionalCode,
            },
          });
          setIsEdgeModalOpen(true);
        }
      } else {
        setSelectedNode(node);
        setIsModalOpen(true);
      }
      return;
    }

    // Close add menu if open
    setAddMenuAnchor(null);
    
    // Decision nodes open the edge modal (for conditional logic)
    if (node.type === 'decision') {
      const decisionEdges = edges.filter(e => e.source === node.id);
      if (decisionEdges.length > 0) {
        setSelectedEdge({
          ...decisionEdges[0],
          source: node.id,
          data: {
            ...decisionEdges[0].data,
            conditionalCode: decisionEdges[0].data?.conditionalCode
          }
        });
        setIsEdgeModalOpen(true);
      }
      return;
    }
    
    // All other nodes open the config modal
    setSelectedNode(node);
    setIsModalOpen(true);
  }, [readonly, edges]);

  // Insert a new node on the outgoing edge of a node (in-place, no full re-layout)
  const handleInsertNode = useCallback((nodeType, fromNodeId = null) => {
    const clickedNodeId = fromNodeId || addMenuAnchor?.nodeId;
    if (!clickedNodeId) return;
    setAddMenuAnchor(null);

    const newNodeId = `${nodeType}_${Date.now()}`;
    const isDecision = nodeType === 'decision';
    const gap = 180;

    // Special case: clicking + on finalize inserts BEFORE finalize
    if (clickedNodeId === 'finalize') {
      const inEdge = edges.find(e => e.target === 'finalize');
      if (!inEdge) return;
      
      const actualSourceId = inEdge.source;
      const sourceNode = nodes.find(n => n.id === actualSourceId);
      const finalizeNode = nodes.find(n => n.id === 'finalize');
      const sourceY = sourceNode?.position?.y ?? 0;
      const insertY = sourceY + gap;

      const newNode = {
        id: newNodeId,
        type: isDecision ? 'decision' : 'workflowNode',
        data: {
          label: toTitleCase(nodeType),
          nodeType,
          status: 'pending',
          configurable: true,
          isInserted: true
        },
        position: {
          x: finalizeNode ? finalizeNode.position.x : 400,
          y: insertY
        }
      };

      const shiftedNodes = nodes.map(n =>
        n.position.y >= insertY
          ? { ...n, position: { ...n.position, y: n.position.y + gap } }
          : n
      );

      const newEdges = edges.filter(e => e.id !== inEdge.id);
      newEdges.push({
        id: `${actualSourceId}_to_${newNodeId}`,
        source: actualSourceId,
        target: newNodeId,
        type: 'smoothstep',
        animated: true,
        sourceHandle: inEdge.sourceHandle || 'bottom',
        targetHandle: 'top'
      });

      if (!isDecision) {
        // Regular node: add edge to finalize
        newEdges.push({
          id: `${newNodeId}_to_finalize`,
          source: newNodeId,
          target: 'finalize',
          type: 'smoothstep',
          animated: true,
          sourceHandle: 'bottom',
          targetHandle: inEdge.targetHandle || 'top'
        });
      }
      // Decision node: no outgoing edges - user must click to define logic

      const updatedNodes = [...shiftedNodes, newNode];
      const distributedEdges = distributeTargetHandles(newEdges, updatedNodes);
      setNodes(updatedNodes);
      setEdges(distributedEdges);
      notifyChange(updatedNodes, distributedEdges, nodeConfigs);
      return;
    }

    // Normal case: insert after the clicked node
    const sourceNodeId = clickedNodeId;
    const outEdge = edges.find(e => e.source === sourceNodeId);
    const targetNodeId = outEdge ? outEdge.target : 'finalize';

    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    const sourceY = sourceNode?.position?.y ?? 0;
    const insertY = sourceY + gap;

    const newNode = {
      id: newNodeId,
      type: isDecision ? 'decision' : 'workflowNode',
      data: {
        label: toTitleCase(nodeType),
        nodeType,
        status: 'pending',
        configurable: true,
        isInserted: true
      },
      position: {
        x: sourceNode ? sourceNode.position.x : 400,
        y: insertY
      }
    };

    // Shift all nodes at or below the insert point downward
    const shiftedNodes = nodes.map(n =>
      n.position.y >= insertY
        ? { ...n, position: { ...n.position, y: n.position.y + gap } }
        : n
    );

    // Remove old edge if it exists
    const newEdges = outEdge ? edges.filter(e => e.id !== outEdge.id) : [...edges];
    
    // Add edge from source to new node
    newEdges.push({
      id: `${sourceNodeId}_to_${newNodeId}`,
      source: sourceNodeId,
      target: newNodeId,
      type: 'smoothstep',
      animated: true,
      sourceHandle: outEdge?.sourceHandle || 'bottom',
      targetHandle: 'top'
    });
    
    if (!isDecision) {
      // Regular node: single outgoing edge
      newEdges.push({
        id: `${newNodeId}_to_${targetNodeId}`,
        source: newNodeId,
        target: targetNodeId,
        type: 'smoothstep',
        animated: true,
        sourceHandle: 'bottom',
        targetHandle: outEdge?.targetHandle || 'top'
      });
    }
    // Decision node: no outgoing edges - user must click to define routing logic

    const updatedNodes = [...shiftedNodes, newNode];
    const distributedEdges = distributeTargetHandles(newEdges, updatedNodes);

    setNodes(updatedNodes);
    setEdges(distributedEdges);
    notifyChange(updatedNodes, distributedEdges, nodeConfigs);
  }, [addMenuAnchor, nodes, edges, nodeConfigs, notifyChange, setNodes, setEdges, distributeTargetHandles]);

  // Add a standalone node (not connected to anything)
  const handleAddStandaloneNode = useCallback((nodeType, position) => {
    const newNodeId = `${nodeType}_${Date.now()}`;
    const isDecision = nodeType === 'decision';

    const newNode = {
      id: newNodeId,
      type: isDecision ? 'decision' : 'workflowNode',
      data: {
        label: toTitleCase(nodeType),
        nodeType,
        status: 'pending',
        configurable: true,
        isInserted: true
      },
      position: position || { x: 400, y: 300 }
    };

    const updatedNodes = [...nodes, newNode];
    setNodes(updatedNodes);
    notifyChange(updatedNodes, edges, nodeConfigs);
  }, [nodes, edges, nodeConfigs, notifyChange, setNodes]);
  
  // Handle edge click for conditional edges
  const onEdgeClick = useCallback((event, edge) => {
    // Conditional edges are inspectable even in readonly (view-only modal).
    if (edge.data?.isConditional) {
      setSelectedEdge(edge);
      setIsEdgeModalOpen(true);
    }
  }, []);
  
  // Define custom node and edge types (AFTER callbacks are defined)
  const nodeTypes = useMemo(() => ({
    workflowNode: WorkflowNode,
    decision: DecisionNode,
    terminal: TerminalNode,
    ...customNodeTypes,
  }), [customNodeTypes]);
  const edgeTypes = useMemo(() => ({
    conditionalEdge: (props) => (
      <ConditionalEdge {...props} onClick={onEdgeClick} />
    ),
    // Read-only preview only (see edgesForFlow below): smoothstep path + a
    // source-anchored, opaque label chip so branch labels stop landing on the
    // node a skip edge routes past.
    previewLabeled: ReadonlyLabeledEdge,
  }), [onEdgeClick]);

  // In read-only preview, route every LABELED edge through ReadonlyLabeledEdge
  // (the "A+B" label fix). Unlabeled edges and the entire editor (readonly
  // false) keep React Flow's default 'smoothstep' edge untouched.
  const edgesForFlow = useMemo(
    () => (readonly
      ? edges.map((e) => (e.label ? { ...e, type: 'previewLabeled' } : e))
      : edges),
    [edges, readonly],
  );
  
  // Handle save config
  const handleSaveConfig = useCallback((config) => {
    if (!selectedNode) return;

    const baseConfig = nodeConfigs[selectedNode.id] || EMPTY_NODE_CONFIG;
    const updatedConfigs = {
      ...nodeConfigs,
      [selectedNode.id]: config
    };

    setNodeConfigs(updatedConfigs);
    notifyChange(nodes, edges, updatedConfigs);

    // Persist as a live per-node override on the deployed pipeline. The callback
    // diffs against `baseConfig` and PATCHes only what changed.
    if (typeof onSaveNodeConfig === 'function') {
      onSaveNodeConfig(selectedNode.id, config, baseConfig);
    }
  }, [selectedNode, nodeConfigs, nodes, edges, notifyChange, onSaveNodeConfig]);
  
  // Handle save conditional edge
  const handleSaveConditionalEdge = useCallback(async ({ conditionalCode, discoveredOutputs, branches = [] }) => {
    if (!selectedEdge) return;
    
    const sourceNodeId = selectedEdge.source;
    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    
    if (!sourceNode) return;
    
    // Remove ALL old edges from the decision node source
    const otherEdges = edges.filter(e => e.source !== sourceNodeId);
    
    // Create new edges for each discovered output
    const newEdges = discoveredOutputs.map((targetId, index) => {
      // Assign colors to different branches (cycle through palette)
      const colors = ['#52c41a', '#ff9800', '#2196f3', '#9c27b0', '#f44336'];
      const strokeColor = discoveredOutputs.length > 1 ? colors[index % colors.length] : '#888888';
      
      // Find matching branch for this target
      const branch = branches.find(b => b.target === targetId);
      const conditionLabel = branch?.condition;
      
      // Label priority: condition text > target node name (only if multiple branches)
      let label = undefined;
      if (discoveredOutputs.length > 1) {
        if (conditionLabel && conditionLabel !== 'default' && conditionLabel !== 'else') {
          // Truncate long conditions
          label = conditionLabel.length > 50 ? conditionLabel.slice(0, 47) + '...' : conditionLabel;
        } else if (conditionLabel === 'else') {
          label = 'else';
        } else {
          // Fallback to target node name
          const targetNode = nodes.find(n => n.id === targetId);
          label = targetNode?.data?.label || targetId;
        }
      }
      
      // Calculate optimal source AND target handles based on positions
      const targetNode = nodes.find(n => n.id === targetId);
      let sourceHandle = 'bottom';
      let targetHandle = 'top';
      
      if (targetNode && sourceNode) {
        // Calculate shortest distance between all valid handle combinations
        const sourcePos = sourceNode.position;
        const targetPos = targetNode.position;
        // Use actual node dimensions if available, otherwise defaults
        const sourceWidth = sourceNode.width || 200;
        const sourceHeight = sourceNode.height || 88;
        const targetWidth = targetNode.width || 200;
        const targetHeight = targetNode.height || 88;
        
        // Define VALID handle positions for each node
        // Source handles: bottom, left, right (NO top - it's target-only!)
        const sourceHandles = {
          bottom: { x: sourcePos.x + sourceWidth / 2, y: sourcePos.y + sourceHeight },
          left: { x: sourcePos.x, y: sourcePos.y + sourceHeight / 2 },
          right: { x: sourcePos.x + sourceWidth, y: sourcePos.y + sourceHeight / 2 }
        };
        
        // Target handles: top, left, right (NO bottom - it's source-only!)
        const targetHandles = {
          top: { x: targetPos.x + targetWidth / 2, y: targetPos.y },
          left: { x: targetPos.x, y: targetPos.y + targetHeight / 2 },
          right: { x: targetPos.x + targetWidth, y: targetPos.y + targetHeight / 2 }
        };
        
        // Find shortest distance combination
        let minDistance = Infinity;
        let bestSourceHandle = 'bottom';
        let bestTargetHandle = 'top';
        
        Object.entries(sourceHandles).forEach(([sHandle, sPos]) => {
          Object.entries(targetHandles).forEach(([tHandle, tPos]) => {
            const distance = Math.sqrt(
              Math.pow(tPos.x - sPos.x, 2) + Math.pow(tPos.y - sPos.y, 2)
            );
            if (distance < minDistance) {
              minDistance = distance;
              bestSourceHandle = sHandle;
              bestTargetHandle = tHandle;
            }
          });
        });
        
        sourceHandle = bestSourceHandle;
        targetHandle = bestTargetHandle;
        
        // Map to actual handle IDs (target handles have 'target-' prefix for left/right)
        if (targetHandle === 'left') targetHandle = 'target-left';
        if (targetHandle === 'right') targetHandle = 'target-right';
      }
      
      return {
        id: `${sourceNodeId}_to_${targetId}`,
        source: sourceNodeId,
        target: targetId,
        type: 'smoothstep',
        sourceHandle,
        targetHandle,
        animated: true,
        style: { 
          stroke: strokeColor,
          strokeWidth: 2
        },
        label,
        data: {
          conditionalCode,
          isConditional: discoveredOutputs.length > 1
        }
      };
    });
    
    const allEdges = [...otherEdges, ...newEdges];
    
    // Don't use distributeTargetHandles - we calculated both handles manually
    setEdges(allEdges);
    notifyChange(nodes, allEdges, nodeConfigs);
  }, [selectedEdge, edges, nodes, nodeConfigs, notifyChange, setEdges]);
  
  // Reset layout - recalculate positions while preserving custom configs
  const handleReset = useCallback(async () => {
    if (readonly) return;
    if (!window.confirm('Reset to default workflow? This will discard all changes and restore the original template.')) {
      return;
    }
    
    // If parent provided a reset handler, use it to fetch fresh template
    if (onRequestReset) {
      onRequestReset();
      return;
    }
    
    // Fallback: reset to initial props
    const { nodes: layoutedNodes, edges: layoutedEdges } = await getLayoutedElements(
      initialNodes,
      initialEdges
    );

    const distributedEdges = distributeTargetHandles(layoutedEdges, layoutedNodes);

    setNodes(layoutedNodes);
    setEdges(distributedEdges);
    setNodeConfigs(initialNodeConfigs);
    notifyChange(layoutedNodes, distributedEdges, initialNodeConfigs);
  }, [readonly, onRequestReset, initialNodes, initialEdges, initialNodeConfigs, notifyChange, setNodes, setEdges, distributeTargetHandles, setNodeConfigs]);
  
  const onPaneClick = useCallback(() => {
    setAddMenuAnchor(null);
    setContextMenu(null);
  }, []);

  // Recalculate decision node edge handles based on current node positions
  const recalculateDecisionEdges = useCallback((currentNodes, currentEdges) => {
    const updatedEdges = currentEdges.map(edge => {
      const sourceNode = currentNodes.find(n => n.id === edge.source);
      const targetNode = currentNodes.find(n => n.id === edge.target);
      
      if (!sourceNode || !targetNode) {
        return edge;
      }
      
      // Calculate shortest distance between all valid handle combinations
      const sourcePos = sourceNode.position;
      const targetPos = targetNode.position;
      // Use actual node dimensions if available, otherwise defaults
      const sourceWidth = sourceNode.width || 200;
      const sourceHeight = sourceNode.height || 88;
      const targetWidth = targetNode.width || 200;
      const targetHeight = targetNode.height || 88;
      
      // Define VALID handle positions for each node
      // Source handles: bottom, left, right (NO top - it's target-only!)
      const sourceHandles = {
        bottom: { x: sourcePos.x + sourceWidth / 2, y: sourcePos.y + sourceHeight },
        left: { x: sourcePos.x, y: sourcePos.y + sourceHeight / 2 },
        right: { x: sourcePos.x + sourceWidth, y: sourcePos.y + sourceHeight / 2 }
      };
      
      // Target handles: top, left, right (NO bottom - it's source-only!)
      const targetHandles = {
        top: { x: targetPos.x + targetWidth / 2, y: targetPos.y },
        left: { x: targetPos.x, y: targetPos.y + targetHeight / 2 },
        right: { x: targetPos.x + targetWidth, y: targetPos.y + targetHeight / 2 }
      };
      
      // Find shortest distance combination
      let minDistance = Infinity;
      let sourceHandle = 'bottom';
      let targetHandle = 'top';
      
      Object.entries(sourceHandles).forEach(([sHandle, sPos]) => {
        Object.entries(targetHandles).forEach(([tHandle, tPos]) => {
          const distance = Math.sqrt(
            Math.pow(tPos.x - sPos.x, 2) + Math.pow(tPos.y - sPos.y, 2)
          );
          if (distance < minDistance) {
            minDistance = distance;
            sourceHandle = sHandle;
            targetHandle = tHandle;
          }
        });
      });
      
      // Map to actual handle IDs (target handles have 'target-' prefix for left/right)
      if (targetHandle === 'left') targetHandle = 'target-left';
      if (targetHandle === 'right') targetHandle = 'target-right';
      
      // Only update if handles changed
      if (edge.sourceHandle === sourceHandle && edge.targetHandle === targetHandle) {
        return edge;
      }
      
      return {
        ...edge,
        sourceHandle,
        targetHandle
      };
    });
    
    return updatedEdges;
  }, []);

  // Disabled live updates during drag to prevent freezing
  // Only recalculate on drag stop
  const onNodeDrag = useCallback((event, draggedNode, allNodes) => {
    // Do nothing - prevents infinite loop and freezing
  }, []);

  // Save node positions after dragging
  const onNodeDragStop = useCallback((event, draggedNode, allNodes) => {
    // React Flow's onNodesChange already updated positions, wait for state to settle
    setTimeout(() => {
      // Use refs to get current state (not stale closure)
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      const currentNodeConfigs = nodeConfigsRef.current;
      const currentNotifyChange = notifyChangeRef.current;
      
      // Merge allNodes (with final drag positions) with current state
      const mergedNodes = currentNodes.map(n => {
        const updatedNode = allNodes.find(an => an.id === n.id);
        return updatedNode || n;
      });
      
      // Recalculate edges with final positions
      const updatedEdges = recalculateDecisionEdges(mergedNodes, currentEdges);
      setEdges(updatedEdges);
      
      // Save to backend
      currentNotifyChange(mergedNodes, updatedEdges, currentNodeConfigs);
    }, 100); // Give React Flow time to update state
  }, [setEdges, recalculateDecisionEdges]);

  // Right-click on canvas to add standalone node
  const onPaneContextMenu = useCallback((event) => {
    if (readonly) return;
    event.preventDefault();
    
    let flowPosition = { x: 400, y: 300 };
    if (reactFlowInstance.current) {
      flowPosition = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });
    }
    
    setContextMenu({
      nodeId: null,
      x: event.clientX,
      y: event.clientY,
      flowPosition
    });
  }, [readonly]);

  // Stable refs so context value never changes identity
  const handleRemoveNodeRef = useRef(handleRemoveNode);
  handleRemoveNodeRef.current = handleRemoveNode;

  const setNodesRef = useRef(setNodes);
  setNodesRef.current = setNodes;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const nodeConfigsRef = useRef(nodeConfigs);
  nodeConfigsRef.current = nodeConfigs;
  const notifyChangeRef = useRef(notifyChange);
  notifyChangeRef.current = notifyChange;

  const workflowActions = useMemo(() => ({
    onRequestAddNode: (nodeId, rect) => {
      setAddMenuAnchor({ nodeId, x: rect.left + rect.width / 2, yBelow: rect.bottom + 6, yAbove: rect.top });
    },
    onRequestRemoveNode: (nodeId) => {
      handleRemoveNodeRef.current(nodeId);
    },
    onRenameNode: (nodeId, newLabel) => {
      setNodesRef.current(nds => nds.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, label: newLabel } } : n
      ));
      const updatedNodes = nodesRef.current.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, label: newLabel } } : n
      );
      notifyChangeRef.current(updatedNodes, edgesRef.current, nodeConfigsRef.current);
    },
    onNodeContextMenu: (nodeId, position) => {
      setContextMenu({ nodeId, x: position.x, y: position.y });
    }
  }), []);

  return (
    <WorkflowActionsContext.Provider value={workflowActions}>
    <div className="gwe-graph-flow-editor">
      <ReactFlow
        nodes={nodesWithAddButton}
        edges={edgesForFlow}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        onInit={(instance) => { reactFlowInstance.current = instance; }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={{ hideAttribution: true }}
        minZoom={0.5}
        maxZoom={1.5}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true
        }}
        nodesDraggable={!readonly}
        nodesConnectable={!readonly}
        elementsSelectable={!readonly}
      >
        <DefaultZoomOut zoomMultiplier={defaultZoomMultiplier} />
        <FitOnSignal signal={fitSignal} />
        <Background
          variant={backgroundVariant}
          color={backgroundColor}
          gap={backgroundGap}
          lineWidth={backgroundLineWidth}
        />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            if (node.data?.status === 'completed' || node.data?.status === 'success') return '#52c41a';
            if (node.data?.status === 'failed' || node.data?.status === 'error') return '#ff4d4f';
            if (node.data?.status === 'running') return '#4a90e2';
            return '#ddd';
          }}
          maskColor="rgba(0, 0, 0, 0.05)"
          // 40% smaller than React Flow's default 200×150.
          style={{ width: 120, height: 90 }}
        />
        
        {!readonly && showResetPanel && (
          <Panel position="top-right" className="gwe-panel">
            <Button 
              variant="outline" 
              size="small" 
              onClick={handleReset}
            >
              Reset to Default
            </Button>
          </Panel>
        )}
        
        {showGraphInfoPanel && (
          <Panel position="top-left" className="gwe-panel">
            <div className="gwe-graph-info">
              <div className="gwe-graph-info__title">
                {graphType === 'analysis' && '🔍 Analysis Workflow'}
                {graphType === 'implementation' && '⚙️ Implementation Workflow'}
                {graphType === 'run_test' && '🧪 Test Run Workflow'}
              </div>
              <div className="gwe-graph-info__hint">
                {readonly ? '👁 View-only mode' : '💡 Click configurable nodes to edit'}
              </div>
            </div>
          </Panel>
        )}
      </ReactFlow>

      {addMenuAnchor && (
        <>
          <div className="gwe-add-menu-backdrop" onClick={() => { setAddMenuAnchor(null); }} />
          <div
            className="gwe-add-menu"
            style={{ left: addMenuAnchor.x, top: addMenuAnchor.yBelow }}
          >
            {insertableCategories.map(cat => (
              <button
                key={cat.id}
                className="gwe-add-menu__item"
                onClick={() => handleInsertNode(cat.nodeType)}
              >
                <span className="gwe-add-menu__item-name">{cat.label}</span>
                <span className="gwe-add-menu__item-desc">{cat.description}</span>
              </button>
            ))}
          </div>
        </>
      )}
      
      <NodeConfigModal
        key={selectedNode?.id}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeData={selectedNode?.data}
        nodeConfig={selectedNode ? (nodeConfigs[selectedNode.id] || EMPTY_NODE_CONFIG) : EMPTY_NODE_CONFIG}
        nodeConfigs={nodeConfigs}
        onSave={handleSaveConfig}
        readOnly={readonly && !nodeConfigEditable}
        nodes={nodes}
        edges={edges}
        selectedNodeId={selectedNode?.id}
        stateSchema={stateSchema}
      />
      
      <ConditionalEdgeModal
        isOpen={isEdgeModalOpen}
        onClose={() => setIsEdgeModalOpen(false)}
        edgeData={selectedEdge}
        availableNodes={nodes}
        edges={edges}
        onSave={handleSaveConditionalEdge}
        readOnly={readonly}
      />

      {contextMenu && (
        <>
          <div className="gwe-add-menu-backdrop" onClick={() => setContextMenu(null)} />
          <div
            className="gwe-add-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            {insertableCategories.map(cat => (
              <button
                key={cat.id}
                className="gwe-add-menu__item"
                onClick={() => {
                  if (contextMenu.nodeId) {
                    // Right-click on node: insert after that node
                    handleInsertNode(cat.nodeType, contextMenu.nodeId);
                  } else {
                    // Right-click on canvas: add standalone node at position
                    handleAddStandaloneNode(cat.nodeType, contextMenu.flowPosition);
                  }
                  setContextMenu(null);
                }}
              >
                <span className="gwe-add-menu__item-name">{cat.label}</span>
                <span className="gwe-add-menu__item-desc">{cat.description}</span>
              </button>
            ))}
            {contextMenu.nodeId && (
              <>
                <div className="gwe-add-menu__divider" />
                <button
                  className="gwe-add-menu__item gwe-add-menu__item--danger"
                  onClick={() => { handleRemoveNode(contextMenu.nodeId); setContextMenu(null); }}
                >
                  <span className="gwe-add-menu__item-name">Delete</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
    </WorkflowActionsContext.Provider>
  );
};
